import React, { useMemo, useState } from "react";
import { Typography, Button, Space, Tooltip, message, Spin, Dropdown, Modal, Input, Tag } from "antd";
import { AppstoreOutlined, BookOutlined, FileTextOutlined, ContainerOutlined, RobotOutlined, CheckCircleOutlined, SyncOutlined, ExclamationCircleOutlined, DownOutlined, SendOutlined, SaveOutlined, StopOutlined, CloseCircleOutlined, SafetyCertificateOutlined, ColumnWidthOutlined, CloseOutlined, AuditOutlined } from "@ant-design/icons";
import { useInvoiceReview } from "../../context/InvoiceReviewContext";
import ReconciliationStats from "./ReconciliationStats";
import ValidationResultModal from "./ValidationResultModal";
import ConfirmableRiskWarningModal, { normalizeRiskWarnings } from "../ConfirmableRiskWarningModal";
import ProductMaintenanceModal from "../shells/ProductMaintenanceModal";
import PromptMaintenanceModal from "../shells/PromptMaintenanceModal";
import BillingRulesMaintenanceModal from "../shells/BillingRulesMaintenanceModal";
import VoidConfirmModal from "../revision/VoidConfirmModal";
import AmountIssuePanel from "./AmountIssuePanel";
import OcrRetryModeNotice, {
    getOcrModeRecoveryAction,
    getOcrRetryOverride,
    getOcrRetryPresentation,
    isHighResolutionPdfOcrPreferred,
    OCR_RETRY_INTENT,
} from "../OcrRetryModeNotice";
import OcrDpiSlider, { OCR_DPI_DEFAULT, normalizeOcrDpi, resolveOcrDpi } from "../OcrDpiSlider";
import workbenchApi from "../../services/workbenchApi";
import PermissionGuard from "../../../../../components/auth/PermissionGuard";
import { getPerIdsPipelineActions } from "../../pipeline";
import { getStandardCostTypeLabel, idsIsTerminal } from "../../../constants";
import {
    buildAmountIssueUpdates,
    findAmountIssues,
    getLineItemFlightAllocationError,
    prepareReviewPayloadForSave,
    resolveLineItemCostType,
    resolveLineItemProductCode,
} from "../../utils/reviewPayloadSemantics";
import "../../../../../styles/layout/page.css";
import "../../../../../styles/utilities/helpers.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ReconciliationHeader = ({ toggleDocumentViewer, documentViewerState, embedded = false, toggleSplitView, splitViewVisible, onCloseDrawer, drawerRecord }) => {
    // 使用 Context 中的共享状态（Single Source of Truth）
    const {
        invoiceDataSourceId,
        reconciliationState,
        updateReconciliationData,
        updateReconciliationState,
        refresh,
        pollingState,
        startPolling,
        stopPolling,
        refreshAfterPolling,
        // 共享的数据源状态
        dataSourceStatus,
        setDataSourceStatus,
        setOcrOptions,
        invoiceDataSource,
        invoices,
        threeTierReadiness,
        amountDeltaClassifications,
        DATA_SOURCE_STATUS_CONFIG,
    } = useInvoiceReview();

    const [productMaintenanceVisible, setProductMaintenanceVisible] = useState(false);
    const [promptMaintenanceVisible, setPromptMaintenanceVisible] = useState(false);
    const [billingRulesMaintenanceVisible, setBillingRulesMaintenanceVisible] = useState(false);
    const [validationModalVisible, setValidationModalVisible] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [confirmableWarningModal, setConfirmableWarningModal] = useState(null);
    const [voidModal, setVoidModal] = useState({ open: false, impact: null });
    const [voidImpactLoading, setVoidImpactLoading] = useState(false);
    const [amountIssuePanelVisible, setAmountIssuePanelVisible] = useState(false);
    // 本地加载状态，用于 Commit 和 Reject 操作
    const [actionLoading, setActionLoading] = useState(false);
    const amountIssues = useMemo(
        () => findAmountIssues(reconciliationState.reconciliationData, {
            classifications: amountDeltaClassifications,
        }),
        [reconciliationState.reconciliationData, amountDeltaClassifications],
    );

    const normalizeCurrency = (value) => {
        if (!value) return null;
        const normalized = String(value).trim().toUpperCase();
        return normalized || null;
    };

    const getInvoiceMeta = (invoice) => invoice?.meta || invoice || {};

    const isEmptyValue = (value) => value === undefined || value === null || (typeof value === "string" && value.trim() === "");

    const isContractCurrencyGateReason = (reason) => {
        const text = String(reason || "");
        return text.includes("不能匹配合同") || text.includes("未匹配到含结算币种的合同");
    };

    const businessShellPredefinedFilters = useMemo(() => {
        const firstInvoice = invoices?.[0];
        const invoiceMeta = firstInvoice?.meta || firstInvoice || {};
        const supplierId = invoiceMeta.supplier_id || invoiceDataSource?.vendor_id;
        const standardCostType = invoiceMeta.standard_cost_type || invoiceDataSource?.standard_cost_type;
        const filters = {};

        if (supplierId) {
            filters.scenario_supplier_id = supplierId;
        }
        if (standardCostType) {
            filters.scenario_standard_cost_type = standardCostType;
        }

        return filters;
    }, [invoiceDataSource?.standard_cost_type, invoiceDataSource?.vendor_id, invoices]);

    const businessShellPredefinedFilterLabels = useMemo(() => {
        const firstInvoice = invoices?.[0];
        const invoiceMeta = firstInvoice?.meta || firstInvoice || {};
        const supplierId = invoiceMeta.supplier_id || invoiceDataSource?.vendor_id;
        const supplierLabel = invoiceMeta.supplier_name || invoiceDataSource?.vendor_name || (supplierId ? String(supplierId) : undefined);
        const standardCostType = invoiceMeta.standard_cost_type || invoiceDataSource?.standard_cost_type;

        return {
            scenario_supplier_label: supplierLabel,
            scenario_standard_cost_type_label: standardCostType ? getStandardCostTypeLabel(standardCostType) : undefined,
        };
    }, [invoiceDataSource?.standard_cost_type, invoiceDataSource?.vendor_id, invoiceDataSource?.vendor_name, invoices]);

    const toFiniteNumber = (value) => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : null;
    };

    const formatInvoiceLabel = (invoice, invoiceIndex) => {
        const invoiceMeta = getInvoiceMeta(invoice);
        const invoiceNumber = typeof invoiceMeta.invoice_number === "string" ? invoiceMeta.invoice_number.trim() : invoiceMeta.invoice_number;
        return invoiceNumber ? `账单${invoiceNumber}` : `第${invoiceIndex + 1}张账单`;
    };

    const validateCommitPayload = (payload) => {
        const invoices = Array.isArray(payload?.invoices) ? payload.invoices : [];

        for (let invoiceIndex = 0; invoiceIndex < invoices.length; invoiceIndex += 1) {
            const invoice = invoices[invoiceIndex];
            const invoiceMeta = getInvoiceMeta(invoice);
            const invoiceLabel = formatInvoiceLabel(invoice, invoiceIndex);

            if (isEmptyValue(invoiceMeta.invoice_number)) {
                return `审核失败：${invoiceLabel}的账单号不能为空`;
            }
            if (isEmptyValue(invoiceMeta.supplier_id)) {
                return `审核失败：${invoiceLabel}的供应商不能为空`;
            }
            if (isEmptyValue(invoiceMeta.currency)) {
                return `审核失败：${invoiceLabel}的币种不能为空`;
            }

            const sections = Array.isArray(invoice.sections) ? invoice.sections : [];
            for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
                const section = sections[sectionIndex];
                const lineItems = Array.isArray(section.line_items) ? section.line_items : [];

                for (let itemIndex = 0; itemIndex < lineItems.length; itemIndex += 1) {
                    const item = lineItems[itemIndex];
                    const itemLabel = `${invoiceLabel}第${sectionIndex + 1}个分节第${itemIndex + 1}条明细`;

                    const flightAllocationError = getLineItemFlightAllocationError(item, itemLabel);
                    if (flightAllocationError) {
                        return flightAllocationError;
                    }

                    const costTypeResolution = resolveLineItemCostType({ item, invoiceMeta, invoiceDataSource });
                    const productCode = resolveLineItemProductCode(item);
                    if (isEmptyValue(costTypeResolution.costType) && isEmptyValue(productCode)) {
                        return `审核失败：${itemLabel}缺少产品代码或费用类型，无法确定标准费用类型`;
                    }

                    const totalPrice = toFiniteNumber(item.total_price);
                    if (totalPrice === null) {
                        return `审核失败：${itemLabel}的总价格式无效或为空`;
                    }
                }
            }
        }

        return null;
    };

    const getThreeTierReadinessError = (payload) => {
        if (!threeTierReadiness || threeTierReadiness.is_ready !== false) return null;

        const readinessInvoices = Array.isArray(threeTierReadiness.invoices) ? threeTierReadiness.invoices : [];
        const manualInputInvoices = readinessInvoices.filter((item) => item?.needs_manual_input);
        if (manualInputInvoices.length > 0) {
            const payloadInvoices = Array.isArray(payload?.invoices) ? payload.invoices : [];
            const hasLocalSettlementAmounts = manualInputInvoices.every((item) => {
                const invoice = payloadInvoices[item.invoice_index];
                const invoiceMeta = getInvoiceMeta(invoice);
                const settlementCurrency = normalizeCurrency(invoiceMeta.settlement_currency);
                const transactionCurrency = normalizeCurrency(item?.transaction_currency);
                if (!transactionCurrency) return false;
                if (settlementCurrency && settlementCurrency === transactionCurrency) {
                    return true;
                }
                const settlementTotal = toFiniteNumber(invoiceMeta.settlement_total_amount);
                return settlementTotal !== null && settlementTotal !== 0;
            });
            if (hasLocalSettlementAmounts) return null;
        }

        const notReadyInvoices = readinessInvoices.filter((item) => item?.is_ready === false);
        const localCurrencyGateInvoices = notReadyInvoices.filter((item) => !item?.needs_manual_input && isContractCurrencyGateReason(item?.missing_reason));
        if (notReadyInvoices.length > 0 && localCurrencyGateInvoices.length === notReadyInvoices.length) {
            const payloadInvoices = Array.isArray(payload?.invoices) ? payload.invoices : [];
            const hasLocalSettlementCurrency = localCurrencyGateInvoices.every((item) => {
                const invoiceMeta = getInvoiceMeta(payloadInvoices[item.invoice_index]);
                return Boolean(normalizeCurrency(invoiceMeta.settlement_currency));
            });
            if (hasLocalSettlementCurrency) {
                const allInvoicesReadyOrAmounted = localCurrencyGateInvoices.every((item) => {
                    const invoice = payloadInvoices[item.invoice_index];
                    const invoiceMeta = getInvoiceMeta(invoice);
                    const settlementCurrency = normalizeCurrency(invoiceMeta.settlement_currency);
                    const transactionCurrency = normalizeCurrency(item?.transaction_currency);
                    if (!transactionCurrency) return false;
                    if (settlementCurrency && settlementCurrency === transactionCurrency) {
                        return true;
                    }
                    const settlementTotal = toFiniteNumber(invoiceMeta.settlement_total_amount);
                    return settlementTotal !== null && settlementTotal !== 0;
                });
                return allInvoicesReadyOrAmounted ? null : "缺少结算金额，请补充后再提交";
            }
        }

        return threeTierReadiness.missing_reason || "缺少结算金额，请补充后再提交";
    };

    const getInvalidMatchedFlightItems = (payload) => {
        const invalidItems = [];
        const invoices = Array.isArray(payload?.invoices) ? payload.invoices : [];

        invoices.forEach((invoice, invoiceIndex) => {
            invoice.sections?.forEach((section) => {
                section.line_items?.forEach((item) => {
                    if (!Array.isArray(item.matched_flights) || item.matched_flights.length === 0) {
                        return;
                    }

                    const nonCompletedFlights = item.matched_flights.filter((flight) => flight.status !== "COMPLETED");
                    if (nonCompletedFlights.length > 0) {
                        invalidItems.push({
                            invoiceIndex,
                            description: item.description,
                            flights: nonCompletedFlights,
                        });
                    }
                });
            });
        });

        return invalidItems;
    };

    const getCommitPrecheckResult = (payload) => {
        const threeTierError = getThreeTierReadinessError(payload);
        if (threeTierError) {
            return {
                invalidMatchedFlights: [],
                validationError: null,
                threeTierError,
            };
        }

        const invalidMatchedFlights = getInvalidMatchedFlightItems(payload);
        if (invalidMatchedFlights.length > 0) {
            return {
                invalidMatchedFlights,
                validationError: null,
                threeTierError: null,
            };
        }

        const validationError = validateCommitPayload(payload);
        if (validationError) {
            return {
                invalidMatchedFlights: [],
                validationError,
                threeTierError: null,
            };
        }

        return {
            invalidMatchedFlights: [],
            validationError: null,
            threeTierError: null,
        };
    };

    const showInvalidMatchedFlightsModal = (invalidItems) => {
        Modal.error({
            title: "无法提交：存在无效的关联航班",
            width: 600,
            content: (
                <div>
                    <p>以下费用项关联了非 COMPLETED 状态的航班，请修正后再提交：</p>
                    <ul style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {invalidItems.map((item, idx) => (
                            <li key={idx} style={{ marginBottom: 8 }}>
                                <strong>{item.description || "未命名项目"}</strong>
                                <ul style={{ color: "#ff4d4f", marginTop: 4 }}>
                                    {item.flights.map((flight, flightIndex) => (
                                        <li key={flightIndex}>
                                            航班 {flight.flight_number} (状态: {flight.status || "未知"})
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
            ),
        });
    };

    const hasUnsavedChanges = reconciliationState.hasUnsavedChanges;
    const batchValidationEligibleStatuses = new Set([
        "VALIDATION_COMPLETED",
        "BATCH_VALIDATION_COMPLETED",
        "BATCH_VALIDATION_FAILED",
    ]);

    // 终态账单不可继续对账操作。
    const isReadOnly = idsIsTerminal(dataSourceStatus);

    // 注意：初始状态由 Context 中的 checkAndStartPolling 获取并设置
    // Header 组件只需消费共享的 dataSourceStatus 即可

    const handleStop = async () => {
        if (!invoiceDataSourceId) return;
        try {
            await workbenchApi.stopPipeline(invoiceDataSourceId);
            message.success("已发送强制停止指令");
            stopPolling();
        } catch (error) {
            message.error("强制停止失败: " + (error.message || "未知错误"));
        }
    };

    // --- Extra Prompt Modal State ---
    const [promptModalVisible, setPromptModalVisible] = useState(false);
    const [promptModalTitle, setPromptModalTitle] = useState("");
    const [currentPromptAction, setCurrentPromptAction] = useState(null); // { type: pipeline stage_id, ocrIntent, callback }
    const [extraPrompt, setExtraPrompt] = useState("");
    const [ocrRetryDpi, setOcrRetryDpi] = useState(OCR_DPI_DEFAULT);
    const [ocrRetryDpiTouched, setOcrRetryDpiTouched] = useState(false);

    const openPromptModal = (title, actionType, callback, ocrIntent = OCR_RETRY_INTENT.INHERIT) => {
        const inheritsHighResolution = actionType === "ocr"
            && ocrIntent === OCR_RETRY_INTENT.INHERIT
            && isHighResolutionPdfOcrPreferred(invoiceDataSource?.ocr_options);
        setPromptModalTitle(title);
        setCurrentPromptAction({ type: actionType, callback, ocrIntent });
        setExtraPrompt("");
        setOcrRetryDpi(ocrIntent === OCR_RETRY_INTENT.HIGH_RESOLUTION || inheritsHighResolution ? resolveOcrDpi(invoiceDataSource?.ocr_options) : OCR_DPI_DEFAULT);
        setOcrRetryDpiTouched(false);
        setPromptModalVisible(true);
    };

    const handlePromptModalOk = async () => {
        if (currentPromptAction && currentPromptAction.callback) {
            const inheritHighResolution = currentPromptAction.type === "ocr"
                && currentPromptAction.ocrIntent === OCR_RETRY_INTENT.INHERIT
                && isHighResolutionPdfOcrPreferred(invoiceDataSource?.ocr_options);
            setPromptModalVisible(false);
            await currentPromptAction.callback(
                extraPrompt,
                currentPromptAction.type === "ocr" ? getOcrRetryOverride(currentPromptAction.ocrIntent) : undefined,
                currentPromptAction.type === "ocr"
                    && (currentPromptAction.ocrIntent === OCR_RETRY_INTENT.HIGH_RESOLUTION || (inheritHighResolution && ocrRetryDpiTouched))
                    ? ocrRetryDpi
                    : undefined,
            );
        }
    };

    const handleRetryAgents = async (stageId, ocrIntent = OCR_RETRY_INTENT.INHERIT) => {
        if (isReadOnly) {
            message.warning("当前状态不可重新执行Agent");
            return;
        }
        if (!invoiceDataSourceId) {
            message.error("未找到账单数据源ID");
            return;
        }

        // stage 名称映射（UI 显示名 → Pipeline stage_id）
        const STAGE_DISPLAY = {
            ocr: { label: "重新 OCR 识别", startMessage: "正在重新识别文件..." },
            parser: { label: "重新制表校验", startMessage: "正在制表校验明细..." },
            allocation: { label: "重新分摊航班", startMessage: "正在重新分摊航班..." },
            validation: { label: "重新验证航班", startMessage: "正在重新验证航班..." },
        };
        const stageInfo = STAGE_DISPLAY[stageId];
        if (!stageInfo) {
            message.error("未知的Agent阶段");
            return;
        }

        const executeAgent = async (prompt = "", highResolutionOverride, pdfRenderDpi) => {
            try {
                const retryArgs = [invoiceDataSourceId, stageId, prompt || undefined, highResolutionOverride];
                if (pdfRenderDpi !== undefined) retryArgs.push(pdfRenderDpi);
                await workbenchApi.retryFromStage(...retryArgs);
                if (stageId === "ocr" && (highResolutionOverride !== undefined || pdfRenderDpi !== undefined)) {
                    setOcrOptions({
                        high_resolution_pdf_ocr: highResolutionOverride !== undefined ? highResolutionOverride : true,
                        ...(highResolutionOverride === false ? {} : { pdf_render_dpi: normalizeOcrDpi(pdfRenderDpi) }),
                        source: "retry_dialog",
                    });
                }
                message.success("已触发Agent重新执行，请稍候...");
                startPolling(stageInfo.startMessage);
            } catch (error) {
                console.error("Trigger failed:", error);
                message.error("触发失败: " + (error.message || "未知错误"));
            }
        };

        const modalTitle = stageId === "ocr"
            ? getOcrRetryPresentation(ocrIntent, invoiceDataSource?.ocr_options).title
            : stageInfo.label;
        openPromptModal(`${modalTitle} - 额外指令`, stageId, executeAgent, ocrIntent);
    };

    // 触发整批核查（详情页入口）
    const handleBatchValidation = async () => {
        if (!invoiceDataSource) {
            message.error("未加载账单数据源信息");
            return;
        }
        const { batch_number, vendor_id, standard_cost_type } = invoiceDataSource;
        if (!batch_number || !vendor_id || !standard_cost_type) {
            message.error("缺少批次信息（batch_number / vendor_id / standard_cost_type），无法触发整批核查");
            return;
        }
        try {
            const result = await workbenchApi.triggerBatchValidation(batch_number, vendor_id, standard_cost_type);
            message.success(result.message || "整批核查已触发");
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "触发整批核查失败";
            message.error(detail);
        }
    };

    const handleOpenVoidWithReparse = async () => {
        if (dataSourceStatus !== "CONFIRMED") {
            message.warning("只有已审核账单可以作废并重发");
            return;
        }
        if (!invoiceDataSourceId) {
            message.error("未找到账单数据源ID");
            return;
        }
        setVoidImpactLoading(true);
        try {
            const impact = await workbenchApi.assessRevisionImpact(invoiceDataSourceId, "IDS");
            setVoidModal({ open: true, impact });
        } catch (error) {
            // 后端 RevisionError 的信息在 data.error；data.detail 是 HTTPException(500) 的字段
            const detail = error?.response?.data?.error || error?.response?.data?.detail || error.message || "影响评估失败";
            message.error(detail);
        } finally {
            setVoidImpactLoading(false);
        }
    };

    const handleConfirmVoidWithReparse = async (reason, mode) => {
        const selectedMode = mode || "copy";
        setActionLoading(true);
        try {
            const result = await workbenchApi.voidInvoiceWithReparse(invoiceDataSourceId, reason, selectedMode);
            const newIdsId = result?.new_ids_id;
            const successMessage = (result?.mode || selectedMode) === "copy"
                ? "已作废，新账单已创建并复制了原数据，请前往审核并修改错误项"
                : newIdsId ? `已作废，新账单 ${newIdsId} 已创建，请前往处理` : "已作废并创建新账单";
            message.success(successMessage);
            setVoidModal({ open: false, impact: null });
            setDataSourceStatus("VOIDED");
            handleRefresh();
        } catch (error) {
            const detail = error?.response?.data?.error || error?.response?.data?.detail || error.message || "作废并重发失败";
            message.error(detail);
        } finally {
            setActionLoading(false);
        }
    };

    const perIdsActionMeta = {
        ocr: {
            label: "重新 OCR 识别",
            icon: <FileTextOutlined />,
        },
        parser: {
            label: "重新制表校验",
            icon: <ContainerOutlined />,
        },
        allocation: {
            label: "重新分摊航班",
            icon: <RobotOutlined />,
        },
        validation: {
            label: "重新验证航班",
            icon: <SafetyCertificateOutlined />,
        },
    };

    const ocrRecoveryAction = getOcrModeRecoveryAction(invoiceDataSource?.ocr_options);
    const promptInheritsHighResolution = currentPromptAction?.type === "ocr"
        && currentPromptAction.ocrIntent === OCR_RETRY_INTENT.INHERIT
        && isHighResolutionPdfOcrPreferred(invoiceDataSource?.ocr_options);
    const promptShowsDpiSlider = currentPromptAction?.type === "ocr"
        && (currentPromptAction.ocrIntent === OCR_RETRY_INTENT.HIGH_RESOLUTION || promptInheritsHighResolution);
    const retryMenuItems = [
        ...getPerIdsPipelineActions(dataSourceStatus)
            .filter((action) => perIdsActionMeta[action.stageId])
            .flatMap((action) => {
                const item = {
                    key: action.stageId,
                    label: perIdsActionMeta[action.stageId].label,
                    icon: perIdsActionMeta[action.stageId].icon,
                    onClick: () => handleRetryAgents(action.stageId),
                    disabled: pollingState.loading || isReadOnly,
                };
                if (action.stageId !== "ocr") return [item];
                return [
                    item,
                    {
                        key: ocrRecoveryAction.key,
                        label: ocrRecoveryAction.label,
                        icon: <FileTextOutlined />,
                        onClick: () => handleRetryAgents("ocr", ocrRecoveryAction.intent),
                        disabled: pollingState.loading || isReadOnly,
                    },
                ];
            }),
        ...(batchValidationEligibleStatuses.has(dataSourceStatus)
            ? [
                  {
                      key: "batch_validation",
                      label: "重新整批核查",
                      icon: <AuditOutlined />,
                      onClick: handleBatchValidation,
                      disabled: pollingState.loading || isReadOnly,
                  },
              ]
            : []),
        {
            type: "divider",
        },
        {
            key: "stop",
            label: "强制停止",
            icon: <StopOutlined />,
            danger: true,
            onClick: handleStop,
            disabled: !pollingState.loading || isReadOnly,
        },
    ];

    // handleRefresh 现在使用 Context 的 refreshAfterPolling
    const handleRefresh = () => {
        refreshAfterPolling();
    };

    // 保存功能
    const handleSave = async (silent = false) => {
        if (isReadOnly && !silent) {
            message.warning("当前状态不可保存修改");
            return false;
        }
        if (reconciliationState.status === "SAVING") return false;
        // 如果没有修改且不是静默保存（强制保存），则直接返回成功
        if (!hasUnsavedChanges && !silent) return true;

        updateReconciliationState({ status: "SAVING" });
        try {
            // 保存前剥离非业务衍生数据，避免持久化到 user_modified_payload：
            // - matched_flights: 后端根据 matched_flight_ids 动态查询生成的航班详情
            // - additional_info 中的旧航班识别字段（flight_date/flight_number 等），
            //   仅用于记录参考，非航班备注字段仍需保留给后端语义解析
            // - match_reasoning: AI 匹配推理说明，同属备注性质
            const dirtyFields = reconciliationState.dirtyFields || new Set();
            const payloadToSave = prepareReviewPayloadForSave(
                reconciliationState.reconciliationData,
                dirtyFields,
            );

            // 调用 AP 专用暂存 API。后端会重新解析当前权威 AAI，避免保存到过期 ActionItem。
            await workbenchApi.saveReviewPayload(invoiceDataSourceId, reconciliationState.actionItemId, payloadToSave);

            updateReconciliationState({
                status: "LOADED",
                hasUnsavedChanges: false,
                // 注意：不清空 dirtyFields！
                // dirtyFields 标记的是"相对于 AI 原始建议的差异"，保存后仍应保持高亮，
                // 让用户持续看到哪些字段是人工修改过的。
                // hasUnsavedChanges 才是"是否有未持久化的修改"。
            });

            await refresh();

            if (!silent) {
                message.success("保存成功");
            }
            return true;
        } catch (error) {
            console.error("Save failed:", error);
            if (!silent) {
                message.error(error.response?.data?.detail || "保存失败");
            }
            updateReconciliationState({ status: "ERROR", error: error.message });
            return false;
        }
    };

    const applyAmountIssueActions = (actions) => {
        if (isReadOnly) {
            message.warning("当前状态不可修改");
            return;
        }
        if (!Array.isArray(actions) || actions.length === 0) {
            return;
        }

        actions.forEach(({ issue, resolution }) => {
            buildAmountIssueUpdates(issue, resolution).forEach(({ path, value }) => {
                updateReconciliationData(path, value);
            });
        });
        message.success(`已处理 ${actions.length} 条金额问题，可暂存修改或直接审核`);
    };

    const executeCommitRequest = async ({ ignoreWarnings }) => {
        try {
            setActionLoading(true);

            const result = await workbenchApi.commitInvoice(invoiceDataSourceId, reconciliationState.actionItemId, {
                ignoreWarnings,
            });

            if (result.success) {
                message.success("审核通过，账单已生成");
                setActionLoading(false);
                setDataSourceStatus("CONFIRMED");
                handleRefresh();
                return;
            }

            setActionLoading(false);

            const riskWarnings = Array.isArray(result.data?.risk_warnings) ? result.data.risk_warnings : [];
            const riskWarningDetails = Array.isArray(result.data?.risk_warning_details) ? result.data.risk_warning_details : [];
            if (riskWarnings.length > 0 || riskWarningDetails.length > 0) {
                setConfirmableWarningModal({
                    warnings: normalizeRiskWarnings(riskWarnings, riskWarningDetails),
                });
                return;
            }

            if (result.data?.errors && result.data.errors.length > 0) {
                Modal.error({
                    title: "提交失败：数据校验未通过",
                    width: 600,
                    content: (
                        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                            <p>{result.message || "请修正以下错误后重试："}</p>
                            <ul style={{ color: "#ff4d4f", paddingLeft: "20px" }}>
                                {result.data.errors.map((err, index) => (
                                    <li key={index} style={{ marginBottom: "4px" }}>
                                        {err}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ),
                });
                return;
            }

            message.error(result.message || "提交失败");
        } catch (error) {
            console.error("Commit failed:", error);
            message.error("提交失败: " + (error.message || "未知错误"));
            setActionLoading(false);
        }
    };

    const submitCommitFlow = async ({ ignoreWarnings = false } = {}) => {
        const effectivePayload = reconciliationState.reconciliationData || {};
        const precheckResult = getCommitPrecheckResult(effectivePayload);

        if (precheckResult.invalidMatchedFlights.length > 0) {
            showInvalidMatchedFlightsModal(precheckResult.invalidMatchedFlights);
            return;
        }

        if (precheckResult.validationError) {
            message.error(precheckResult.validationError);
            return;
        }

        if (precheckResult.threeTierError) {
            message.error(precheckResult.threeTierError);
            return;
        }

        if (hasUnsavedChanges) {
            const saveSuccess = await handleSave(true);
            if (!saveSuccess) {
                return;
            }
        }

        await executeCommitRequest({
            ignoreWarnings,
        });
    };

    // 审核提交功能
    const handleCommit = async (ignoreWarnings = false) => {
        if (isReadOnly) {
            message.warning("当前状态不可提交审核");
            return;
        }
        if (!reconciliationState.actionItemId) {
            message.error("未找到行动项ID");
            return;
        }

        await submitCommitFlow({ ignoreWarnings });
    };

    // 审核拒绝功能
    const handleReject = async () => {
        if (isReadOnly) {
            message.warning("当前状态不可拒绝");
            return;
        }
        if (!rejectReason.trim()) {
            message.warning("请输入拒绝原因");
            return;
        }

        if (!reconciliationState.actionItemId) {
            message.error("未找到行动项ID");
            return;
        }

        try {
            setActionLoading(true);

            const result = await workbenchApi.rejectInvoice(invoiceDataSourceId, reconciliationState.actionItemId, rejectReason);

            if (result.success) {
                message.success("已拒绝该账单");
                setRejectModalVisible(false);
                setRejectReason("");
                setActionLoading(false);
                setDataSourceStatus("REJECTED"); // Update local status
                handleRefresh();
            } else {
                message.error(result.message || "拒绝失败");
                setActionLoading(false);
            }
        } catch (error) {
            console.error("Reject failed:", error);
            message.error("拒绝失败: " + (error.message || "未知错误"));
            setActionLoading(false);
        }
    };

    // --- 渲染左侧信息区域 ---
    const renderLeftSection = () => {
        if (embedded && drawerRecord) {
            // 嵌入模式：关闭按钮 + 文件名 + 状态 Tag + 费用类型 Tag
            const filename = drawerRecord.original_filename || "账单详情";
            return (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {onCloseDrawer && (
                        <Tooltip title="关闭">
                            <Button type="text" size="small" icon={<CloseOutlined />} onClick={onCloseDrawer} style={{ color: "#8c8c8c" }} />
                        </Tooltip>
                    )}
                    <Text strong style={{ fontSize: 14, maxWidth: 260 }} ellipsis={{ tooltip: filename }}>
                        {filename}
                    </Text>
                    {invoiceDataSource?.standard_cost_type && (
                        <Tooltip title="费用类型（预分类）">
                            <Tag color="geekblue" style={{ margin: 0 }}>
                                {invoiceDataSource.standard_cost_type}
                            </Tag>
                        </Tooltip>
                    )}
                    {dataSourceStatus && DATA_SOURCE_STATUS_CONFIG[dataSourceStatus] && (
                        <Tag color={DATA_SOURCE_STATUS_CONFIG[dataSourceStatus].color} style={{ margin: 0 }}>
                            {DATA_SOURCE_STATUS_CONFIG[dataSourceStatus].label}
                        </Tag>
                    )}
                    <ReconciliationStats />
                </div>
            );
        }

        // standalone 模式：大标题 + 统计信息
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <Title level={4} style={{ margin: 0 }}>
                    应付对账复核
                    {dataSourceStatus && DATA_SOURCE_STATUS_CONFIG[dataSourceStatus] && (
                        <Tag color={DATA_SOURCE_STATUS_CONFIG[dataSourceStatus].color} style={{ marginLeft: 8 }}>
                            {DATA_SOURCE_STATUS_CONFIG[dataSourceStatus].label}
                        </Tag>
                    )}
                    {invoiceDataSource?.standard_cost_type && (
                        <Tooltip title="费用类型（预分类）">
                            <Tag color="geekblue" style={{ marginLeft: 8 }}>
                                {invoiceDataSource.standard_cost_type}
                            </Tag>
                        </Tooltip>
                    )}
                </Title>
                <ReconciliationStats />
            </div>
        );
    };

    return (
        <div
            style={{
                background: "#fff",
                padding: embedded ? "8px 16px" : "8px var(--spacing-lg)",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                // 工具栏按钮较多，窄屏/抽屉下允许换行，避免横向溢出屏幕
                flexWrap: "wrap",
                gap: "8px 12px",
                minHeight: embedded ? "48px" : "64px",
                flexShrink: 0,
            }}
        >
            {renderLeftSection()}

            {/* 全局工具栏 */}
            <Space size="middle" wrap style={{ marginLeft: "auto" }}>
                {/* 查看类按钮 - 简化样式 */}
                <Space size="small" wrap>
                    <Tooltip title="维护当前供应商与费用类型的产品编码和识别提示">
                        <Button type={productMaintenanceVisible ? "primary" : "text"} icon={<AppstoreOutlined />} onClick={() => setProductMaintenanceVisible(true)}>
                            产品维护
                        </Button>
                    </Tooltip>
                    <Tooltip title="维护当前供应商与费用类型适用的 Agent 提示词">
                        <Button type={promptMaintenanceVisible ? "primary" : "text"} icon={<FileTextOutlined />} onClick={() => setPromptMaintenanceVisible(true)}>
                            提示词维护
                        </Button>
                    </Tooltip>
                    <Tooltip title="维护当前供应商与费用类型的计费规则">
                        <Button type={billingRulesMaintenanceVisible ? "primary" : "text"} icon={<BookOutlined />} onClick={() => setBillingRulesMaintenanceVisible(true)}>
                            计费规则
                        </Button>
                    </Tooltip>
                    <Tooltip title="查看账单源文件 (Alt+1)">
                        <Button type={documentViewerState.type === "invoice" && documentViewerState.visible ? "primary" : "text"} icon={<FileTextOutlined />} onClick={() => toggleDocumentViewer("invoice")}>
                            账单
                        </Button>
                    </Tooltip>
                    <Tooltip title="左侧查看 PDF 原件，右侧处理对账数据">
                        <Button type={splitViewVisible ? "primary" : "text"} icon={<ColumnWidthOutlined />} onClick={toggleSplitView}>
                            分屏视图
                        </Button>
                    </Tooltip>
                    {/* TODO: 检查按钮暂时隐藏，后续可能恢复
                    <Tooltip title="查看航班费用验证结果">
                        <Button type={validationModalVisible ? "primary" : "text"} icon={<SafetyCertificateOutlined />} onClick={() => setValidationModalVisible(true)}>
                            检查
                        </Button>
                    </Tooltip>
                    */}
                </Space>

                <div style={{ width: 1, height: 24, background: "#f0f0f0" }} />

                {/* Retry Agents Dropdown */}
                {pollingState.status === "completed" ? (
                    <Tooltip title="智能分析完成，点击刷新页面">
                        <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleRefresh} style={{ backgroundColor: "var(--success-color)", borderColor: "var(--success-color)" }}>
                            刷新数据
                        </Button>
                    </Tooltip>
                ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        {pollingState.status === "polling" && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <Spin indicator={<SyncOutlined spin />} size="small" style={{ marginRight: 4 }} />
                                {pollingState.message}
                            </Text>
                        )}
                        {pollingState.status === "failed" && (
                            <Tooltip title="点击重试">
                                <Text type="danger" style={{ fontSize: 12, cursor: "pointer" }} onClick={() => handleRetryAgents("ocr")}>
                                    <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                    执行失败
                                </Text>
                            </Tooltip>
                        )}

                        {/* 旧的独立页面入口已废弃，不再提供新标签页跳转 */}

                        {dataSourceStatus === "CONFIRMED" && (
                            <PermissionGuard permission="settlement:revision:write">
                                <Button danger icon={<CloseCircleOutlined />} loading={actionLoading || voidImpactLoading} onClick={handleOpenVoidWithReparse}>
                                    作废并重发
                                </Button>
                            </PermissionGuard>
                        )}

                        {/* 重新智能分析下拉 - 需要 settlement_ap_reconciliation:create 权限 */}
                        <PermissionGuard permission="settlement_ap_reconciliation:create">
                            <Dropdown
                                menu={{
                                    items: retryMenuItems,
                                }}
                            >
                                <Button icon={<RobotOutlined />} loading={pollingState.loading} disabled={isReadOnly}>
                                    重新解析 <DownOutlined />
                                </Button>
                            </Dropdown>
                        </PermissionGuard>

                        {/* 暂存修改按钮 - 需要 settlement_ap_reconciliation:update 权限 */}
                        <PermissionGuard permission="settlement_ap_reconciliation:update">
                            <Tooltip title={isReadOnly ? "当前状态不可修改" : hasUnsavedChanges ? "有未保存的修改" : "暂无修改"}>
                                <Button
                                    ghost={hasUnsavedChanges && !isReadOnly}
                                    icon={hasUnsavedChanges && !isReadOnly ? <SaveOutlined style={{ color: "#1890ff" }} /> : <SaveOutlined />}
                                    loading={reconciliationState.status === "SAVING"}
                                    disabled={!hasUnsavedChanges || isReadOnly}
                                    onClick={() => handleSave(false)}
                                    style={
                                        hasUnsavedChanges && !isReadOnly
                                            ? {
                                                  borderColor: "#1890ff",
                                                  color: "#1890ff",
                                              }
                                            : undefined
                                    }
                                >
                                    暂存修改
                                </Button>
                            </Tooltip>
                        </PermissionGuard>

                        {/* 审核拒绝按钮 - 需要 settlement_ap_invoice:commit 权限 */}
                        <PermissionGuard permission="settlement_ap_invoice:commit">
                            <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectModalVisible(true)} loading={actionLoading} disabled={isReadOnly}>
                                审核拒绝
                            </Button>
                        </PermissionGuard>

                        {/* 金额问题统一入口紧邻审核通过，避免形成独立放行路径。 */}
                        <PermissionGuard permission="settlement_ap_reconciliation:update">
                            <Tooltip title={amountIssues.length > 0 ? `还有 ${amountIssues.length} 条金额问题需要处理` : "当前没有待处理的金额问题"}>
                                <Button
                                    icon={<ExclamationCircleOutlined />}
                                    danger={amountIssues.length > 0}
                                    disabled={amountIssues.length === 0}
                                    onClick={() => setAmountIssuePanelVisible(true)}
                                >
                                    待处理项（{amountIssues.length}）
                                </Button>
                            </Tooltip>
                        </PermissionGuard>

                        {/* 审核通过按钮 - 需要 settlement_ap_invoice:commit 权限 */}
                        <PermissionGuard permission="settlement_ap_invoice:commit">
                            <Button icon={<SendOutlined />} type="primary" onClick={() => handleCommit(false)} loading={actionLoading} disabled={isReadOnly}>
                                审核通过
                            </Button>
                        </PermissionGuard>
                    </div>
                )}
            </Space>

            {/* 验证结果弹窗 */}
            <ValidationResultModal visible={validationModalVisible} onClose={() => setValidationModalVisible(false)} />

            <ProductMaintenanceModal
                visible={productMaintenanceVisible}
                onClose={() => setProductMaintenanceVisible(false)}
                onSaved={() => {
                    setProductMaintenanceVisible(false);
                    refresh();
                }}
                predefinedFilters={businessShellPredefinedFilters}
                predefinedFilterLabels={businessShellPredefinedFilterLabels}
            />
            <PromptMaintenanceModal
                visible={promptMaintenanceVisible}
                onClose={() => setPromptMaintenanceVisible(false)}
                predefinedFilters={businessShellPredefinedFilters}
                predefinedFilterLabels={businessShellPredefinedFilterLabels}
            />
            <BillingRulesMaintenanceModal
                visible={billingRulesMaintenanceVisible}
                onClose={() => setBillingRulesMaintenanceVisible(false)}
                predefinedFilters={businessShellPredefinedFilters}
                predefinedFilterLabels={businessShellPredefinedFilterLabels}
            />

            {/* 拒绝原因模态框 */}
            <Modal
                title="拒绝账单"
                open={rejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setRejectModalVisible(false);
                    setRejectReason("");
                }}
                okText="确认拒绝"
                okButtonProps={{ danger: true, loading: actionLoading }}
                cancelText="取消"
            >
                <p>请输入拒绝原因：</p>
                <TextArea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="例如：账单信息错误、重复报销等" />
            </Modal>

            <ConfirmableRiskWarningModal
                open={Boolean(confirmableWarningModal)}
                title="存在可确认审核风险"
                warnings={confirmableWarningModal?.warnings || []}
                loading={actionLoading}
                onCancel={() => setConfirmableWarningModal(null)}
                onConfirm={() => {
                    const modalState = confirmableWarningModal;
                    if (!modalState) return undefined;
                    setConfirmableWarningModal(null);
                    return submitCommitFlow({
                        ignoreWarnings: true,
                    });
                }}
            />

            <AmountIssuePanel
                open={amountIssuePanelVisible}
                issues={amountIssues}
                readOnly={isReadOnly}
                onClose={() => setAmountIssuePanelVisible(false)}
                onApply={applyAmountIssueActions}
            />

            <VoidConfirmModal
                open={voidModal.open}
                title="作废并重发"
                targetLabel={invoiceDataSource?.original_filename || invoiceDataSourceId}
                impact={voidModal.impact}
                impactLoading={voidImpactLoading}
                confirmText="确认作废并重发"
                submitting={actionLoading}
                onCancel={() => setVoidModal({ open: false, impact: null })}
                onConfirm={handleConfirmVoidWithReparse}
            />

            {/* Extra Prompt Input Modal */}
            <Modal title={promptModalTitle} open={promptModalVisible} onOk={handlePromptModalOk} onCancel={() => setPromptModalVisible(false)} okText="确认执行" cancelText="取消">
                <div style={{ marginBottom: 16 }}>
                    <p>请输入额外的指令或规则（可选）：</p>
                    <Input.TextArea rows={4} value={extraPrompt} onChange={(e) => setExtraPrompt(e.target.value)} placeholder="例如：请特别注意检查税率是否为6%..." />
                </div>
                {currentPromptAction?.type === "ocr" && (
                    <OcrRetryModeNotice
                        intent={currentPromptAction.ocrIntent}
                        ocrOptions={invoiceDataSource?.ocr_options}
                        pdfRenderDpi={ocrRetryDpi}
                    />
                )}
                {promptShowsDpiSlider && (
                    <OcrDpiSlider
                        value={ocrRetryDpi}
                        onChange={(nextDpi) => {
                            setOcrRetryDpi(nextDpi);
                            setOcrRetryDpiTouched(true);
                        }}
                    />
                )}
            </Modal>
        </div>
    );
};

export default ReconciliationHeader;
